/**
 * HTML Cleaner page (html.html).
 *
 * Paste messy rich text on the left, get clean HTML / Markdown / plain text on
 * the right. Everything runs in this tab — nothing is uploaded, same as the rest
 * of the site.
 */

import './styles/main.css';
import './styles/main-html.css';

import { initTheme } from '@/lib/theme';
import { initConsent } from '@/lib/consent';
import { createThemeToggle } from '@/components/ThemeToggle';
import { createPalettePicker } from '@/components/PalettePicker';
import { createModal } from '@/components/Modal';
import { getAboutHTML } from '@/components/AboutContent';
import { getPrivacyHTML } from '@/components/PrivacyContent';
import { maybeShowConsentBanner, openConsentBanner } from '@/components/ConsentBanner';
import { toast } from '@/components/Toast';

import {
  convertDocument,
  sanitizeHtml,
  htmlToMarkdown,
  htmlToText,
  htmlToDocxBlob,
  markdownToHtml,
  tidyHtml,
  DEFAULT_OPTIONS,
  PRESETS,
  type CleanOptions,
  type InputMode,
  type OutputFormat,
} from '@/lib/htmlclean';

import { SETTING_GROUPS, type SettingKey } from '@/components/htmlclean/settings-schema';
import { SAMPLE_INPUT } from '@/components/htmlclean/sample';

initTheme();
initConsent();

// ── State ────────────────────────────────────────────────────────────────────

const STORAGE_KEY = 'converter.htmlclean.v1';

interface UiState {
  options: CleanOptions;
  inputMode: InputMode;
  outputView: 'visual' | 'code';
}

function loadState(): UiState {
  const fallback: UiState = { options: { ...DEFAULT_OPTIONS }, inputMode: 'rich', outputView: 'code' };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return fallback;
    const saved = JSON.parse(raw) as Partial<UiState>;
    return {
      options: { ...DEFAULT_OPTIONS, ...(saved.options ?? {}) },
      inputMode: saved.inputMode ?? 'rich',
      outputView: saved.outputView ?? 'code',
    };
  } catch {
    return fallback;
  }
}

function saveState(): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch { /* private mode / quota — not worth bothering the user about */ }
}

const state = loadState();

// ── DOM shell ────────────────────────────────────────────────────────────────

const app = document.getElementById('app');
if (!app) throw new Error('#app element not found');

const header = document.createElement('header');
header.className = 'rd-header';
header.innerHTML = `
  <div class="rd-header__brand">
    <a href="/" class="rd-header__logo rd-header__logo--link">Image Converter</a>
    <span class="rd-header__tagline">HTML cleaner</span>
  </div>
  <div class="rd-header__right">
    <span class="rd-header__badge">100% Local</span>
  </div>
`;

const backBtn = document.createElement('a');
backBtn.className = 'rd-btn rd-btn--secondary';
backBtn.href = '/';
backBtn.textContent = 'Images';
header.querySelector('.rd-header__right')?.appendChild(backBtn);
header.querySelector('.rd-header__right')?.appendChild(createPalettePicker());
header.querySelector('.rd-header__right')?.appendChild(createThemeToggle());

const main = document.createElement('main');
main.className = 'rd-main hc-main';
main.id = 'main';

const left = document.createElement('div');
left.className = 'rd-left hc-settings';

const right = document.createElement('div');
right.className = 'rd-right hc-panes';

main.appendChild(left);
main.appendChild(right);

// ── Input pane ───────────────────────────────────────────────────────────────

const inputPane = document.createElement('section');
inputPane.className = 'hc-pane';
inputPane.innerHTML = `
  <div class="hc-pane__head">
    <span class="hc-pane__title">Input</span>
    <div class="rd-unit-toggle hc-input-modes" role="group" aria-label="Input type">
      <button type="button" class="rd-unit-btn" data-mode="rich">Rich text</button>
      <button type="button" class="rd-unit-btn" data-mode="html">HTML</button>
      <button type="button" class="rd-unit-btn" data-mode="markdown">Markdown</button>
    </div>
    <div class="hc-pane__spacer"></div>
    <button type="button" class="rd-btn rd-btn--ghost" data-action="sample">Example</button>
    <button type="button" class="rd-btn rd-btn--ghost" data-action="clear">Clear</button>
  </div>
  <div class="hc-pane__body">
    <div class="hc-editor" contenteditable="true" role="textbox" aria-multiline="true"
         aria-label="Paste rich text here" spellcheck="false"></div>
    <textarea class="hc-source" spellcheck="false" aria-label="Paste source code here"
              placeholder="Paste HTML or Markdown here…"></textarea>
  </div>
  <div class="hc-pane__foot"><span class="hc-count" data-role="in-count"></span></div>
`;

const editor = inputPane.querySelector<HTMLElement>('.hc-editor')!;
const sourceBox = inputPane.querySelector<HTMLTextAreaElement>('.hc-source')!;
const inCount = inputPane.querySelector<HTMLElement>('[data-role="in-count"]')!;

// ── Output pane ──────────────────────────────────────────────────────────────

const outputPane = document.createElement('section');
outputPane.className = 'hc-pane';
outputPane.innerHTML = `
  <div class="hc-pane__head">
    <span class="hc-pane__title">Output</span>
    <div class="rd-unit-toggle hc-output-formats" role="group" aria-label="Output format">
      <button type="button" class="rd-unit-btn" data-format="text">Text</button>
      <button type="button" class="rd-unit-btn" data-format="html">HTML</button>
      <button type="button" class="rd-unit-btn" data-format="markdown">Markdown</button>
      <button type="button" class="rd-unit-btn" data-format="word">Word</button>
    </div>
    <div class="hc-pane__spacer"></div>
    <div class="hc-view-group">
      <span class="hc-view-label">View</span>
      <div class="rd-unit-toggle hc-output-views" role="group" aria-label="Output view">
        <button type="button" class="rd-unit-btn" data-view="visual">Visual</button>
        <button type="button" class="rd-unit-btn" data-view="code">Code</button>
      </div>
    </div>
    <div class="hc-actions" data-role="actions"></div>
  </div>
  <div class="hc-pane__body">
    <div class="hc-preview" aria-label="Rendered output"></div>
    <pre class="hc-code"><code></code></pre>
  </div>
  <div class="hc-pane__foot">
    <span class="hc-count" data-role="out-count"></span>
    <span class="hc-word-hint" data-role="word-hint" hidden>Word (.docx) is a file — use <strong>Download</strong> or <strong>Copy → For Docs</strong>.</span>
  </div>
`;

const preview = outputPane.querySelector<HTMLElement>('.hc-preview')!;
const codeBox = outputPane.querySelector<HTMLElement>('.hc-code > code')!;
const outCount = outputPane.querySelector<HTMLElement>('[data-role="out-count"]')!;
const wordHint = outputPane.querySelector<HTMLElement>('[data-role="word-hint"]')!;
const actionsWrap = outputPane.querySelector<HTMLElement>('[data-role="actions"]')!;
const formatButtons = Array.from(outputPane.querySelectorAll<HTMLButtonElement>('[data-format]'));

right.appendChild(inputPane);
right.appendChild(outputPane);

// ── Settings panel ───────────────────────────────────────────────────────────

const settingsCard = document.createElement('div');
settingsCard.className = 'rd-card hc-card';

const presetRow = document.createElement('div');
presetRow.className = 'hc-presets';
presetRow.innerHTML = `<p class="rd-card__title hc-card__title">Presets</p>`;
const presetButtons = document.createElement('div');
presetButtons.className = 'hc-preset-buttons';
for (const preset of PRESETS) {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'rd-btn rd-btn--secondary hc-preset';
  btn.textContent = preset.label;
  btn.title = preset.hint;
  btn.addEventListener('click', () => {
    Object.assign(state.options, preset.patch);
    syncControls();
    run();
    toast.info(`${preset.label}: ${preset.hint}`);
  });
  presetButtons.appendChild(btn);
}
presetRow.appendChild(presetButtons);
settingsCard.appendChild(presetRow);

/** Every checkbox, keyed by option name, so presets can push values back in. */
const checkboxes = new Map<SettingKey, HTMLInputElement>();

for (const group of SETTING_GROUPS) {
  const title = document.createElement('p');
  title.className = 'rd-card__title hc-card__title';
  title.textContent = group.title;
  settingsCard.appendChild(title);

  const list = document.createElement('div');
  list.className = 'hc-option-list';

  for (const opt of group.options) {
    const label = document.createElement('label');
    label.className = 'rd-checkbox-label hc-option';
    label.title = opt.hint;

    const box = document.createElement('input');
    box.type = 'checkbox';
    box.className = 'rd-checkbox';
    box.checked = state.options[opt.key] as boolean;
    box.addEventListener('change', () => {
      (state.options[opt.key] as boolean) = box.checked;
      run();
    });
    checkboxes.set(opt.key, box);

    const text = document.createElement('span');
    text.textContent = opt.label;

    label.appendChild(box);
    label.appendChild(text);
    list.appendChild(label);
  }

  settingsCard.appendChild(list);
}

// Allowed-tags field
const allowedTitle = document.createElement('p');
allowedTitle.className = 'rd-card__title hc-card__title';
allowedTitle.textContent = 'Keep only these tags';
settingsCard.appendChild(allowedTitle);

const allowedWrap = document.createElement('div');
allowedWrap.className = 'hc-option-list';
const allowedInput = document.createElement('input');
allowedInput.type = 'text';
allowedInput.className = 'hc-text-input';
allowedInput.placeholder = 'Blank = keep every safe tag';
allowedInput.value = state.options.allowedTags;
allowedInput.addEventListener('input', () => {
  state.options.allowedTags = allowedInput.value;
  scheduleRun();
});
const allowedHint = document.createElement('p');
allowedHint.className = 'hc-hint';
allowedHint.textContent = 'e.g. h2 h3 p ul ol li strong em a — anything else is unwrapped, its text kept.';
allowedWrap.appendChild(allowedInput);
allowedWrap.appendChild(allowedHint);
settingsCard.appendChild(allowedWrap);

// HTML output options (the format itself now lives in the Output pane header).
const outTitle = document.createElement('p');
outTitle.className = 'rd-card__title hc-card__title';
outTitle.textContent = 'HTML output';
settingsCard.appendChild(outTitle);

const formatWrap = document.createElement('div');
formatWrap.className = 'hc-option-list';

const tidyLabel = document.createElement('label');
tidyLabel.className = 'rd-checkbox-label hc-option';
tidyLabel.title = 'Put each block on its own line and indent nested tags. HTML output only.';
const tidyBox = document.createElement('input');
tidyBox.type = 'checkbox';
tidyBox.className = 'rd-checkbox';
tidyBox.checked = state.options.tidy;
tidyBox.addEventListener('change', () => {
  state.options.tidy = tidyBox.checked;
  run();
});
const tidyText = document.createElement('span');
tidyText.textContent = 'Tidy / indent the HTML';
tidyLabel.appendChild(tidyBox);
tidyLabel.appendChild(tidyText);
formatWrap.appendChild(tidyLabel);

settingsCard.appendChild(formatWrap);

const note = document.createElement('p');
note.className = 'hc-hint hc-hint--foot';
note.textContent =
  'No spell-check or AI rewriting here — this tool fixes structure and spacing, '
  + 'not wording. Scripts, event handlers and javascript: links are always removed.';
settingsCard.appendChild(note);

left.appendChild(settingsCard);

// ── Input mode + view switching ──────────────────────────────────────────────

const modeButtons = Array.from(inputPane.querySelectorAll<HTMLButtonElement>('[data-mode]'));
const viewButtons = Array.from(outputPane.querySelectorAll<HTMLButtonElement>('[data-view]'));

for (const btn of modeButtons) {
  btn.addEventListener('click', () => setInputMode(btn.dataset.mode as InputMode));
}
for (const btn of viewButtons) {
  btn.addEventListener('click', () => {
    if (btn.disabled) return;
    state.outputView = btn.dataset.view as 'visual' | 'code';
    syncControls();
    render();
    saveState();
  });
}
for (const btn of formatButtons) {
  btn.addEventListener('click', () => {
    state.options.format = btn.dataset.format as OutputFormat;
    syncControls();
    run();
  });
}

/** Read whatever the active input is showing. */
function readInput(): string {
  return state.inputMode === 'rich' ? editor.innerHTML : sourceBox.value;
}

/**
 * Switch input type, carrying the current content across. Going to Markdown
 * converts through the cleaner, so the user doesn't lose their work when they
 * change their mind about how they want to type.
 */
function setInputMode(next: InputMode): void {
  if (next === state.inputMode) return;

  const from = state.inputMode;
  const content = readInput();
  const hasContent = !!content.trim();

  if (hasContent) {
    // Everything is normalised to HTML first, then rendered into the new mode.
    const asHtml = from === 'markdown' ? markdownToHtml(content) : content;

    if (next === 'rich') {
      editor.innerHTML = sanitizeHtml(asHtml);
    } else if (next === 'html') {
      sourceBox.value = tidyHtml(sanitizeHtml(asHtml));
    } else {
      sourceBox.value = htmlToMarkdown(sanitizeHtml(asHtml));
    }
  }

  state.inputMode = next;
  syncControls();
  run();
}

/** Push `state` back onto every control — used after presets and mode swaps. */
function syncControls(): void {
  for (const [key, box] of checkboxes) box.checked = state.options[key] as boolean;
  tidyBox.checked = state.options.tidy;
  allowedInput.value = state.options.allowedTags;

  const isWord = state.options.format === 'word';

  // Word is a binary file — there is no raw text to show, so the Code view is
  // off and the pane always shows the rendered preview.
  if (isWord) state.outputView = 'visual';

  for (const btn of modeButtons) {
    btn.classList.toggle('rd-unit-btn--active', btn.dataset.mode === state.inputMode);
  }
  for (const btn of viewButtons) {
    const isCode = btn.dataset.view === 'code';
    btn.disabled = isWord && isCode;
    btn.classList.toggle('rd-unit-btn--active', btn.dataset.view === state.outputView && !btn.disabled);
  }
  for (const btn of formatButtons) {
    btn.classList.toggle('rd-unit-btn--active', btn.dataset.format === state.options.format);
  }

  wordHint.hidden = !isWord;

  const rich = state.inputMode === 'rich';
  editor.hidden = !rich;
  sourceBox.hidden = rich;
  sourceBox.placeholder = state.inputMode === 'markdown'
    ? 'Paste Markdown here…'
    : 'Paste HTML source here…';

  // Only HTML output can be indented.
  tidyBox.disabled = state.options.format !== 'html';
  tidyLabel.classList.toggle('hc-option--disabled', tidyBox.disabled);

  // Highlight which menu entry the plain Download / Copy buttons map to.
  downloadBtn.setActive(DOWNLOAD_DEFAULT[state.options.format]);
  copyBtn.setActive(COPY_DEFAULT[state.options.format]);
}

// ── Conversion ───────────────────────────────────────────────────────────────

let lastOutput = '';
let lastHtml = '';

function run(): void {
  const input = readInput();
  const { output, html } = convertDocument(input, state.inputMode, state.options);
  lastOutput = output;
  lastHtml = html;
  render();
  saveState();
}

function render(): void {
  const showVisual = state.outputView === 'visual';
  preview.hidden = !showVisual;
  codeBox.parentElement!.hidden = showVisual;

  if (showVisual) {
    // Plain text has no markup to render — show it as-is, spacing preserved.
    if (state.options.format === 'text') {
      preview.textContent = lastOutput;
      preview.classList.add('hc-preview--plain');
    } else {
      const html = state.options.format === 'markdown' ? markdownToHtml(lastOutput) : lastHtml;
      preview.innerHTML = sanitizeHtml(html);
      preview.classList.remove('hc-preview--plain');
    }
  } else {
    codeBox.textContent = lastOutput;
  }

  const inChars = readInput().length;
  const outChars = lastOutput.length;
  inCount.textContent = inChars ? `${formatCount(inChars)} characters` : '';
  outCount.textContent = outChars
    ? `${formatCount(outChars)} characters${inChars ? ` · ${percentChange(inChars, outChars)}` : ''}`
    : '';
}

function formatCount(n: number): string {
  return n.toLocaleString();
}

function percentChange(before: number, after: number): string {
  const pct = Math.round((1 - after / before) * 100);
  if (pct > 0) return `${pct}% smaller`;
  if (pct < 0) return `${-pct}% larger`;
  return 'same size';
}

/** Typing fires constantly — wait for a pause before re-converting. */
let runTimer: number | undefined;
function scheduleRun(): void {
  window.clearTimeout(runTimer);
  runTimer = window.setTimeout(run, 180);
}

editor.addEventListener('input', scheduleRun);
sourceBox.addEventListener('input', scheduleRun);

/**
 * Take over paste in the rich editor. The browser would happily drop in the
 * source page's `<script>`/`onerror` markup, so the HTML is run through the
 * safety pass before it reaches the document.
 */
editor.addEventListener('paste', (e: ClipboardEvent) => {
  const data = e.clipboardData;
  if (!data) return;
  e.preventDefault();

  const html = data.getData('text/html');
  const text = data.getData('text/plain');

  if (html) {
    insertHtmlAtCursor(sanitizeHtml(html));
  } else if (text) {
    document.execCommand('insertText', false, text);
  }
  scheduleRun();
});

function insertHtmlAtCursor(html: string): void {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) {
    editor.innerHTML += html;
    return;
  }
  const range = selection.getRangeAt(0);
  range.deleteContents();
  const fragment = range.createContextualFragment(html);
  const last = fragment.lastChild;
  range.insertNode(fragment);
  if (last) {
    range.setStartAfter(last);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
  }
}

// ── Pane buttons ─────────────────────────────────────────────────────────────

inputPane.querySelector('[data-action="clear"]')?.addEventListener('click', () => {
  editor.innerHTML = '';
  sourceBox.value = '';
  run();
});

inputPane.querySelector('[data-action="sample"]')?.addEventListener('click', () => {
  if (state.inputMode === 'rich') editor.innerHTML = sanitizeHtml(SAMPLE_INPUT);
  else if (state.inputMode === 'html') sourceBox.value = SAMPLE_INPUT.trim();
  else sourceBox.value = htmlToMarkdown(SAMPLE_INPUT);
  run();
});

/**
 * A button with a caret that drops a small menu of alternatives. The main
 * button does the default action; the menu offers the other formats.
 */
interface SplitMenuItem { id: string; label: string; }

function createSplitButton(config: {
  variant: 'primary' | 'secondary';
  label: string;
  items: SplitMenuItem[];
  onMain: () => void;
  onItem: (id: string) => void;
}): { root: HTMLElement; setActive: (id: string) => void } {
  const root = document.createElement('div');
  root.className = 'hc-split';

  const mainBtn = document.createElement('button');
  mainBtn.type = 'button';
  mainBtn.className = `rd-btn rd-btn--${config.variant} hc-split__main`;
  mainBtn.textContent = config.label;
  mainBtn.addEventListener('click', config.onMain);

  const caret = document.createElement('button');
  caret.type = 'button';
  caret.className = `rd-btn rd-btn--${config.variant} hc-split__caret`;
  caret.setAttribute('aria-haspopup', 'menu');
  caret.setAttribute('aria-expanded', 'false');
  caret.setAttribute('aria-label', `${config.label} options`);
  caret.innerHTML = `<svg viewBox="0 0 20 20" width="12" height="12" aria-hidden="true">
    <path fill="currentColor" d="M5 7l5 6 5-6z"/></svg>`;

  const menu = document.createElement('div');
  menu.className = 'hc-split__menu';
  menu.setAttribute('role', 'menu');
  menu.hidden = true;

  const itemEls = new Map<string, HTMLButtonElement>();
  for (const it of config.items) {
    const el = document.createElement('button');
    el.type = 'button';
    el.className = 'hc-split__item';
    el.setAttribute('role', 'menuitem');
    el.textContent = it.label;
    el.addEventListener('click', () => { close(); config.onItem(it.id); });
    itemEls.set(it.id, el);
    menu.appendChild(el);
  }

  function open(): void {
    menu.hidden = false;
    caret.setAttribute('aria-expanded', 'true');
    document.addEventListener('click', onDocClick, true);
    document.addEventListener('keydown', onKey);
  }
  function close(): void {
    menu.hidden = true;
    caret.setAttribute('aria-expanded', 'false');
    document.removeEventListener('click', onDocClick, true);
    document.removeEventListener('keydown', onKey);
  }
  function onDocClick(e: MouseEvent): void {
    if (!root.contains(e.target as Node)) close();
  }
  function onKey(e: KeyboardEvent): void {
    if (e.key === 'Escape') close();
  }
  caret.addEventListener('click', (e) => {
    e.stopPropagation();
    if (menu.hidden) open(); else close();
  });

  root.appendChild(mainBtn);
  root.appendChild(caret);
  root.appendChild(menu);

  return {
    root,
    setActive(id: string): void {
      for (const [key, el] of itemEls) {
        el.classList.toggle('hc-split__item--active', key === id);
      }
    },
  };
}

// ── Output generation (built on demand from the cleaned HTML) ─────────────────

/** Nothing to act on when the cleaned output is empty. */
function hasOutput(): boolean {
  return lastOutput.trim() !== '' || lastHtml.trim() !== '';
}

function asText(): string { return htmlToText(lastHtml); }
function asMarkdown(): string { return htmlToMarkdown(lastHtml); }
function asHtmlSource(): string { return state.options.tidy ? tidyHtml(lastHtml) : lastHtml; }

/** Wrap a body fragment in a minimal standalone HTML document. */
function fullHtmlDocument(bodyHtml: string): string {
  return '<!doctype html>\n<html lang="en">\n<head>\n<meta charset="utf-8">\n</head>\n'
    + `<body>\n${bodyHtml}\n</body>\n</html>\n`;
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Which menu entry the plain Copy / Download buttons fall back to per format. */
const COPY_DEFAULT: Record<OutputFormat, string> = {
  text: 'plain', html: 'html', markdown: 'markdown', word: 'docs',
};
const DOWNLOAD_DEFAULT: Record<OutputFormat, string> = {
  text: 'text', html: 'html', markdown: 'markdown', word: 'word',
};

async function copyAs(id: string): Promise<void> {
  if (!hasOutput()) { toast.info('Nothing to copy yet.'); return; }
  try {
    if (id === 'docs') {
      const richHtml = fullHtmlDocument(lastHtml);
      if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
        await navigator.clipboard.write([
          new ClipboardItem({
            'text/html': new Blob([richHtml], { type: 'text/html' }),
            'text/plain': new Blob([asText()], { type: 'text/plain' }),
          }),
        ]);
      } else {
        // Old browsers with no rich clipboard — fall back to the HTML source.
        await navigator.clipboard.writeText(asHtmlSource());
      }
      toast.info('Copied — paste into Google Docs or Word to keep the formatting.');
      return;
    }
    const text = id === 'plain' ? asText() : id === 'markdown' ? asMarkdown() : asHtmlSource();
    await navigator.clipboard.writeText(text);
    toast.info('Copied to clipboard.');
  } catch {
    toast.error('Could not copy — your browser blocked clipboard access.');
  }
}

async function downloadAs(id: string): Promise<void> {
  if (!hasOutput()) { toast.info('Nothing to download yet.'); return; }
  try {
    if (id === 'word') {
      const blob = await htmlToDocxBlob(lastHtml);
      triggerDownload(blob, 'cleaned.docx');
      return;
    }
    let text: string;
    let ext: string;
    let mime: string;
    if (id === 'text') {
      text = asText(); ext = 'txt'; mime = 'text/plain';
    } else if (id === 'markdown') {
      text = asMarkdown(); ext = 'md'; mime = 'text/markdown';
    } else {
      text = fullHtmlDocument(asHtmlSource()); ext = 'html'; mime = 'text/html';
    }
    triggerDownload(new Blob([text], { type: `${mime};charset=utf-8` }), `cleaned.${ext}`);
  } catch {
    toast.error('Could not build that file.');
  }
}

const copyBtn = createSplitButton({
  variant: 'primary',
  label: 'Copy',
  items: [
    { id: 'plain', label: 'Plain text (no formatting)' },
    { id: 'docs', label: 'For Docs / Word (rich text)' },
    { id: 'html', label: 'HTML' },
    { id: 'markdown', label: 'Markdown' },
  ],
  onMain: () => void copyAs(COPY_DEFAULT[state.options.format]),
  onItem: (id) => void copyAs(id),
});

const downloadBtn = createSplitButton({
  variant: 'secondary',
  label: 'Download',
  items: [
    { id: 'text', label: 'Text (.txt)' },
    { id: 'html', label: 'HTML (.html)' },
    { id: 'markdown', label: 'Markdown (.md)' },
    { id: 'word', label: 'Word (.docx)' },
  ],
  onMain: () => void downloadAs(DOWNLOAD_DEFAULT[state.options.format]),
  onItem: (id) => void downloadAs(id),
});

actionsWrap.appendChild(downloadBtn.root);
actionsWrap.appendChild(copyBtn.root);

// ── Modals + footer (shared with the main page) ──────────────────────────────

const aboutModal = createModal({ title: 'About', contentHtml: getAboutHTML() });
const privacyModal = createModal({ title: 'Privacy Notice', contentHtml: getPrivacyHTML() });

aboutModal.element.addEventListener('click', (e) => {
  const a = (e.target as HTMLElement).closest<HTMLAnchorElement>('a.about-privacy-link');
  if (a) {
    e.preventDefault();
    aboutModal.close();
    privacyModal.open();
  }
});

const footer = document.createElement('footer');
footer.className = 'rd-footer';

const BUILD_VERSION_HTML = (() => {
  const sha = __BUILD_SHA__;
  const date = __BUILD_DATE__;
  if (sha && sha !== 'dev') {
    return `Build ${date} · <a href="https://github.com/RadGH/Offline-File-Converter/commit/${sha}" class="rd-footer__link" target="_blank" rel="noopener noreferrer">${sha}</a>`;
  }
  return `Build ${date} · ${sha}`;
})();

footer.innerHTML = `
  <div class="rd-footer__inner">
    <p class="rd-footer__line rd-footer__privacy">Everything runs in your browser · Nothing is uploaded</p>
    <div class="rd-footer__line rd-footer__line--links">
      <a href="/" class="rd-footer__link">Image converter</a>
      <a href="#" class="rd-footer__link" data-action="open-about">About</a>
      <a href="#" class="rd-footer__link" data-action="open-privacy">Privacy</a>
      <a href="#" class="rd-footer__link" data-action="manage-cookies">Manage cookies</a>
    </div>
    <p class="rd-footer__line rd-footer__version">${BUILD_VERSION_HTML}</p>
  </div>
`;

footer.querySelector('[data-action="open-about"]')?.addEventListener('click', (e) => {
  e.preventDefault();
  aboutModal.open();
});
footer.querySelector('[data-action="open-privacy"]')?.addEventListener('click', (e) => {
  e.preventDefault();
  privacyModal.open();
});
footer.querySelector('[data-action="manage-cookies"]')?.addEventListener('click', (e) => {
  e.preventDefault();
  openConsentBanner();
});

app.appendChild(header);
app.appendChild(main);
app.appendChild(footer);

maybeShowConsentBanner();

syncControls();
run();
